import { useState } from 'react';
import { GraphQLClient } from 'graphql-request';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { getToken } from './services/auth/index.js';
import './utils/logToSlack.js';
import { fetchPolicyLookup } from './utils/fetchPolicyLookup.js';
import { getCookies, setCookie } from './utils/cookies.js';
import './utils/currency.js';
import './context/index.js';
import { b as COOKIE_CURRENCY_NAME, a as COOKIE_LANGUAGE_NAME, l as COOKIE_PROMO_TAG } from './cookies-l-jEMp6P.js';
import './currency-_kacFJHO.js';
import { S as SITE_LANGUAGES, a as LanguageCode } from './language-BHHwgtQp.js';
import './payment-YC9H491Y.js';
import 'next/server';
import './index-Oip0gjfj.js';
import './product-_pRduMz7.js';
import 'next/router';
import './helpers-kA_hdoOI.js';
import 'lodash';

/* eslint-disable @typescript-eslint/no-explicit-any */
// Create a Set of valid language codes for efficient lookups
const LANGUAGE_CODES_SET = new Set(SITE_LANGUAGES);
/**
 * Type guard function to check if a string is a valid LanguageCode.
 * This provides type safety by checking against the actual valid language codes
 * instead of using unsafe type assertions.
 *
 * @param value The string to check
 * @returns True if the value is a valid LanguageCode, false otherwise
 */
const isLanguageCode = (value) => LANGUAGE_CODES_SET.has(value);
const graphqlRequestClient = (uri) => {
    const client = new GraphQLClient(uri);
    return client;
};
/**
 * Normalize a raw locale string to a supported `LanguageCode`.
 *
 * Behavior:
 * - If `locale` is undefined, null, or an empty string, returns `undefined` (caller may choose a default later).
 * - The literal value `'default'` is treated as `'en'` for backward compatibility.
 * - If the normalized value is a supported `LanguageCode`, that code is returned.
 * - Otherwise, the function falls back to `LanguageCode.En`.
 *
 * Examples:
 * - normalizeLocale(undefined) -> undefined
 * - normalizeLocale('default') -> LanguageCode.En
 * - normalizeLocale('fr') -> LanguageCode.Fr (if defined in `LanguageCode`)
 * - normalizeLocale('unknown') -> LanguageCode.En
 *
 * @param locale Raw locale from cookies, query variables, or SSR context.
 * @returns A `LanguageCode` when `locale` is provided; `undefined` if not provided. For unknown values, returns `LanguageCode.En`.
 */
const normalizeLocale = (locale) => {
    if (!locale)
        return undefined;
    const normalized = locale === 'default' ? 'en' : locale;
    return isLanguageCode(normalized) ? normalized : LanguageCode.En;
};
const getHeaders = (Store, options = {}, ctx, isPrivate = false, fetchPolicyKey) => {
    var _a;
    const cookies = getCookies(ctx);
    const currency = (ctx === null || ctx === void 0 ? void 0 : ctx.currency) || cookies[COOKIE_CURRENCY_NAME] || '';
    const cookieLocale = normalizeLocale(cookies[COOKIE_LANGUAGE_NAME]);
    // Get "locale" param from a cookie
    // Prefer explicit locale passed in query variables when running client-side
    const rawLocaleValue = get(options, ['variables', 'locale']);
    const variableLocaleRaw = typeof rawLocaleValue === 'string' ? rawLocaleValue : undefined;
    const resolvedVariableLocale = normalizeLocale(variableLocaleRaw);
    const ctxLocaleRaw = typeof (ctx === null || ctx === void 0 ? void 0 : ctx.locale) === 'string' ? ctx.locale : undefined;
    const ctxLocaleNormalized = normalizeLocale(ctxLocaleRaw);
    const locale = resolvedVariableLocale || ctxLocaleNormalized || cookieLocale || LanguageCode.En;
    // Get "store" param based on "locale"
    const store = Store[locale] || Store.default;
    const promoTag = ((_a = ctx === null || ctx === void 0 ? void 0 : ctx.query) === null || _a === void 0 ? void 0 : _a['promo-tag']) || cookies[COOKIE_PROMO_TAG] || null;
    if (!cookies[COOKIE_PROMO_TAG] && promoTag) {
        setCookie(COOKIE_PROMO_TAG, promoTag, ctx);
    }
    if (!cookies[COOKIE_CURRENCY_NAME] && currency) {
        setCookie(COOKIE_CURRENCY_NAME, currency, ctx);
    }
    // Keep language cookie in sync with the effective locale when changed via URL
    if (cookies[COOKIE_LANGUAGE_NAME] !== locale && typeof locale === 'string') {
        setCookie(COOKIE_LANGUAGE_NAME, locale, ctx);
    }
    const authToken = getToken(ctx);
    const optionsCopy = cloneDeep(options);
    const currentHeaders = get(optionsCopy, ['context', 'headers'], {});
    const fetchPolicy = fetchPolicyKey ? fetchPolicyLookup(fetchPolicyKey) : 'no-cache';
    // Extract operation name and variables
    let operationName = fetchPolicyKey || '';
    const variables = JSON.stringify((optionsCopy === null || optionsCopy === void 0 ? void 0 : optionsCopy.variables) || {});
    // Check if the operation name is "GetPromotion" and add the current date in Paris time zone
    if (operationName === 'GetPromotion') {
        const parisTimeZone = 'Europe/Paris';
        const currentDate = new Intl.DateTimeFormat('en-GB', {
            timeZone: parisTimeZone,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(new Date());
        operationName = `${operationName} - ${currentDate}`;
    }
    if (operationName === 'GetCurrencies') {
        operationName = `${operationName} - ${locale}`;
    }
    if (operationName === 'GetYmal') {
        operationName = `${operationName} - ${locale} - ${currency}`;
    }
    const isCacheDisabled = process.env.NEXT_PUBLIC_CACHE_DISABLED === 'true';
    // Be careful not to add any headers with a potentially null value
    // The following headers are used as cache keys for Varnish cache: Store, promo-tag, Content-Currency, CacheKeyOperation, CacheKeyVariables
    const headers = {
        ...currentHeaders,
        Store: store,
        'Content-Currency': currency,
        ...(promoTag !== null && !isCacheDisabled ? { 'promo-tag': promoTag } : {}),
        ...(operationName !== null && fetchPolicy !== 'no-cache' && !isCacheDisabled
            ? { CacheKeyOperation: operationName }
            : {}),
        ...(variables !== null && fetchPolicy !== 'no-cache' && !isCacheDisabled
            ? { CacheKeyVariables: encodeURIComponent(variables) }
            : {}),
    };
    if (isPrivate) {
        headers.Authorization = `Bearer ${authToken}`;
    }
    return headers;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useQuery = (uri, query, apolloProps, isPrivate, fetchPolicyKey) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const func = options => {
        setError(null);
        setLoading(true);
        const headers = getHeaders(apolloProps.store, options, null, isPrivate, fetchPolicyKey);
        return graphqlRequestClient(uri)
            .request(query, options === null || options === void 0 ? void 0 : options.variables, headers)
            .then(data => ({ data, error: undefined }))
            .catch((e) => {
            setError(e);
            return { data: undefined, error: e };
        })
            .finally(() => {
            setLoading(false);
        });
    };
    return [func, { loading, error }];
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useMutation = (uri, query, headers) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const func = options => {
        setError(null);
        setLoading(true);
        return graphqlRequestClient(uri)
            .request(query, options === null || options === void 0 ? void 0 : options.variables, headers)
            .then(data => ({ data, error: undefined }))
            .catch((e) => {
            setError(e);
            return { data: undefined, error: e };
        })
            .finally(() => {
            setLoading(false);
        });
    };
    return [func, { loading, error }];
};

export { getHeaders as a, useMutation as b, graphqlRequestClient as g, useQuery as u };
//# sourceMappingURL=graphqlRequestClient-3KYHZKVm.js.map
